import { getDriverId, IAutoPolicy, TPolicy } from '@sureapp/platform-sdk';
import { findLatestByKey } from '@sureapp/wl-toggle-common/dist/services/array';
import isEmpty from 'lodash/isEmpty';

import {
  isRentersQuote,
  isAutoQuoteCheckout,
  isAutoQuoteVars,
  isCmsError,
  isAutoPolicy,
} from '@@/common/services/typeGuards';

import { EUserFlow } from '../interfaces';

import type { TAppStoreState } from '.';

export const getAutoQuote = (state: TAppStoreState) =>
  isRentersQuote(state.quote) ? null : state.quote;

export const getAutoNoPriorContext = (state: TAppStoreState) => {
  const autoQuote = getAutoQuote(state);
  return {
    reason: autoQuote?.application?.no_prior_reason,
    isQuestionnaireComplete: !!autoQuote?.application?.no_prior_reason,
    isNoPriorEnabled:
      !!autoQuote?.application?.metadata?.requires_no_prior_questionnaire,
  };
};

export const getAutoPolicy = (state: TAppStoreState) =>
  isAutoPolicy(state.policy) ? state.policy : null;

export const getAutoPolicyCheckout = (state: TAppStoreState) =>
  isAutoQuoteCheckout(state.policyCheckout) ? state.policyCheckout : null;

export const getStagedDirectBill = (state: TAppStoreState) => {
  if (isEmpty(state.stagedDirectBill)) {
    return null;
  }
  return state.stagedDirectBill;
};

export const getPolicyBalance = (state: TAppStoreState) => {
  return state.policyBalance;
};

export const getStagedAutoPolicy = (state: TAppStoreState) => {
  if (isEmpty(state.stagedAutoPolicy)) {
    return null;
  }
  return isAutoPolicy(state.stagedAutoPolicy as TPolicy)
    ? (state.stagedAutoPolicy as IAutoPolicy)
    : null;
};

export const getAutoPolicyDriverById =
  (id?: string | number | null) => (state: TAppStoreState) => {
    if (!id) {
      return undefined;
    }

    const stagedPolicy = getStagedAutoPolicy(state);
    const policy = getAutoPolicy(state);
    return ((stagedPolicy ?? policy)?.details?.drivers ?? []).find(
      (driver) => getDriverId(driver) === id
    );
  };

export const getAutoQuoteCheckout = (state: TAppStoreState) =>
  isAutoQuoteCheckout(state.quoteCheckout) ? state.quoteCheckout : null;

export const getAutoQuoteDriverById =
  (id?: string | number | null) => (state: TAppStoreState) => {
    if (!id) {
      return undefined;
    }
    const quote = getAutoQuote(state);
    return (quote?.application?.drivers ?? []).find(
      (driver) => driver.id === id
    );
  };

export const getAutoDriverByUserFlowAndId =
  (userFlow: EUserFlow, id?: string | number | null) =>
  (state: TAppStoreState) => {
    if (userFlow === 'quote-flow') {
      return getAutoQuoteDriverById(id)(state);
    }

    if (userFlow === 'self-service') {
      return getAutoPolicyDriverById(id)(state);
    }

    return undefined;
  };

export const getAutoQuoteVars = (state: TAppStoreState) =>
  isAutoQuoteVars(state.quoteVars) ? state.quoteVars : null;

export const getCmsContent = (modelSlug: string) => (state: TAppStoreState) =>
  state.cmsContent?.[modelSlug];

export const getCmsEntry =
  (modelSlug: string, entrySlug: string) => (state: TAppStoreState) => {
    const modelContent = getCmsContent(modelSlug)(state);

    return isCmsError(modelContent)
      ? undefined
      : modelContent?.entries?.find((entry) => entry.slug === entrySlug);
  };

export const getEnrollmentApplication = (state: TAppStoreState) =>
  state.enrollmentApplication;

export const getFeatureFlags = (state: TAppStoreState) => state.featureFlags;

export const getIsAgentUsingQuoteAssist = (store: TAppStoreState) =>
  store.isAgentUsingQuoteAssist;

export const getIsCcpaBannerOpen = (state: TAppStoreState) =>
  state.isCcpaBannerOpen;

export const getIsLoggedIn = (state: TAppStoreState) => !!state.oktaAccessToken;

export const getOktaAccessToken = (state: TAppStoreState) =>
  state.oktaAccessToken;

export const getOktaIdToken = (state: TAppStoreState) => state.oktaIdToken;

export const getPlans = (store: TAppStoreState) => store.plans;

export const getPolicies = (store: TAppStoreState) => store.policies;

export const getAutoPolicies = (store: TAppStoreState) => {
  return getPolicies(store).filter(
    (policy) =>
      policy?.vertical?.code !== 'toggle_homeowners' &&
      policy?.vertical?.code !== 'farmers_renters'
  );
};

export const getRentersPolicies = (store: TAppStoreState) => {
  return getPolicies(store).filter(
    (policy) => policy?.vertical?.code === 'farmers_renters'
  );
};

// TODO: ch233062 Update type from any to TPolicy type when SDK is updated
export const getMostRecentAutoPolicy = (store: TAppStoreState) => {
  const autoPolicies = getAutoPolicies(store);
  return findLatestByKey(autoPolicies, 'created_at');
};

export const getMostRecentRentersPolicy = (store: TAppStoreState) => {
  const rentersPolicies = getRentersPolicies(store);
  return findLatestByKey(rentersPolicies, 'created_at');
};

export const getPurchaseInfo = (state: TAppStoreState) => state.purchaseInfo;

export const getQuoteData = (state: TAppStoreState) => state.quoteData;

export const getRentersQuote = (state: TAppStoreState) =>
  isRentersQuote(state.quote) ? state.quote : undefined;

export const getStashedAddEditVehicleFormValues = (state: TAppStoreState) =>
  state.stashedAddEditVehicleFormValues;

export const getUrlParams = (store: TAppStoreState) => store.urlParams;

export const getUser = (store: TAppStoreState) => store.user;

export const migratedPolicySearchInfo = (store: TAppStoreState) =>
  store.migratedPolicySearchInfo;
